import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Badge,
  Chip,
  Menu,
  MenuItem
} from "@material-ui/core";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { splitOccurrenceNotes } from "../../utils/occurrenceAdvanced";
import OccurrenceIcon from "./OccurrenceIcon";
import {
  formatOccurrenceDate,
  formatOccurrenceWhen,
  occurrenceSortTime
} from "./occurrenceDisplay";

const StackWrap = styled.div`
  position: relative;
  height: 228px;
`;

const StackShadow = styled(Paper)`
  position: absolute;
  left: ${props => props.offset}px;
  top: ${props => props.offset}px;
  width: 100%;
  height: 220px;
  z-index: 0;
`;

const CardFace = styled(Paper)`
  position: relative;
  z-index: 1;
  height: 220px;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  overflow: hidden;
  border-left: 4px solid ${props => props.$accent || "#546E7A"};
`;

const CardText = styled.div`
  margin-left: 12px;
  min-width: 0;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
`;

const CardTitle = styled(Typography)`
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const CardPreview = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  margin: 8px 0 6px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f4f6f8;
  overflow: hidden;
`;

const CardPreviewFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 32px;
  background: linear-gradient(180deg, rgba(244, 246, 248, 0) 0%, #f4f6f8 72%);
  pointer-events: none;
`;

const CardMore = styled.span`
  position: absolute;
  right: 8px;
  bottom: 4px;
  z-index: 1;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.45);
`;

const CardNotes = styled(Typography)`
  font-size: 13px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.72);
  overflow-wrap: anywhere;
`;

const CardDate = styled(Typography)`
  flex-shrink: 0;
  margin-top: auto;
`;

const MenuRow = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-width: 280px;
`;

const MenuTitle = styled.span`
  min-width: 0;
`;

const MenuDate = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  white-space: nowrap;
`;

export default function OccurrenceStackCard({
  stack,
  primaryColor,
  dateLocale,
  onSelect
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { latest } = stack;
  const hasStack = stack.count > 1;
  const { notes } = splitOccurrenceNotes(latest.description);
  const stageLabel = String(latest.stageShortName || "").trim();
  const accent = latest.color || primaryColor;
  const hasOverflow = notes.length > 72;

  const openLatest = () => {
    if (onSelect) onSelect(latest);
  };
  const handleClick = event => {
    if (!onSelect) return;
    if (hasStack) {
      setAnchorEl(event.currentTarget);
      return;
    }
    openLatest();
  };

  return (
    <>
      <StackWrap>
        {hasStack && <StackShadow elevation={0} offset={6} />}
        {hasStack && <StackShadow elevation={0} offset={3} />}
        <CardFace
          elevation={2}
          $accent={accent}
          onClick={handleClick}
          style={{ cursor: onSelect ? "pointer" : "default" }}
        >
          <Badge
            badgeContent={hasStack ? stack.count : 0}
            color="primary"
            overlap="circle"
          >
            <OccurrenceIcon
              occurrence={latest}
              fallbackColor={primaryColor}
              size={36}
            />
          </Badge>
          <CardText>
            <CardHeader>
              <CardTitle>{latest.title || latest.reason}</CardTitle>
              {stageLabel ? <Chip size="small" label={stageLabel} /> : null}
            </CardHeader>
            {latest.studentName ? (
              <Typography variant="body2" color="textSecondary" noWrap>
                {latest.studentName}
              </Typography>
            ) : null}
            {notes ? (
              <CardPreview>
                <CardNotes>{notes}</CardNotes>
                {hasOverflow ? (
                  <>
                    <CardPreviewFade />
                    <CardMore>{t("occurrences.advanced.cardMore")}</CardMore>
                  </>
                ) : null}
              </CardPreview>
            ) : (
              <Box flex={1} />
            )}
            <CardDate variant="caption" color="textSecondary">
              {formatOccurrenceDate(latest, dateLocale, t)}
            </CardDate>
          </CardText>
        </CardFace>
      </StackWrap>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {[latest, ...stack.rest]
          .slice()
          .sort((a, b) => occurrenceSortTime(b) - occurrenceSortTime(a))
          .map(item => (
            <MenuItem
              key={item.id || `${item.date}-${item.reason}`}
              onClick={() => {
                setAnchorEl(null);
                onSelect(item);
              }}
            >
              <MenuRow>
                <MenuTitle>
                  {item.title || item.reason}
                  {item.studentName ? ` — ${item.studentName}` : ""}
                </MenuTitle>
                <MenuDate>{formatOccurrenceWhen(item, dateLocale, t)}</MenuDate>
              </MenuRow>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}

OccurrenceStackCard.propTypes = {
  stack: PropTypes.shape({
    latest: PropTypes.object.isRequired,
    rest: PropTypes.arrayOf(PropTypes.object).isRequired,
    count: PropTypes.number.isRequired
  }).isRequired,
  primaryColor: PropTypes.string.isRequired,
  dateLocale: PropTypes.string.isRequired,
  onSelect: PropTypes.func
};

OccurrenceStackCard.defaultProps = {
  onSelect: undefined
};
